import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Plus, Trash2 } from 'lucide-react';

const REQUESTED_ACTION_OPTIONS = [
  { value: 'approval', label: 'Approval' },
  { value: 'comment', label: 'Comment' },
  { value: 'endorsement', label: 'Endorsement' },
  { value: 'information_only', label: 'Information Only' },
];

const RESPONSIBLE_GROUPS = [
  { value: 'secretariat', label: 'Secretariat Team' },
  { value: 'board_committee', label: 'Board / Committees' },
  { value: 'other_officials', label: 'Other Officials' },
  { value: 'individual', label: 'Individual Person' },
];

export const ConsoleItemDetailsTab = ({
  selectedItem, setSelectedItem, updateAgendaItem, users,
  newAI, setNewAI, addActionItemToDetail, removeActionItemFromDetail,
}) => {
  if (!selectedItem) {
    return <div className="text-center py-8 text-[#5A6570]"><FileText className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" /><p>Select an agenda item from the Approved Agenda tab</p></div>;
  }

  const getUserName = (uid) => users.find(u => u.id === uid)?.full_name || 'Unknown';

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-[#1A1D21]">{selectedItem.title}</h3>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs px-2 py-1 rounded bg-[#00A3A3]/10 text-[#00A3A3]">{selectedItem.item_type.replace('_', ' ')}</span>
          {selectedItem.presenter && <span className="text-sm text-[#5A6570]">Presenter: {getUserName(selectedItem.presenter)}</span>}
        </div>
      </div>

      {/* 1) Issues/Concerns */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21]">1. Issues / Concerns Sought to be Addressed</Label>
        <Textarea value={selectedItem.issues_concerns || ''} onChange={e => setSelectedItem({ ...selectedItem, issues_concerns: e.target.value })}
          onBlur={() => updateAgendaItem(selectedItem.id, { issues_concerns: selectedItem.issues_concerns })}
          placeholder="Describe the issues or concerns..." className="mt-1 border-[#E2E6EA] min-h-[60px]" data-testid="issues-concerns" />
      </div>

      {/* 2) Proposals/Recommendations */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21]">2. Proposals / Recommendations</Label>
        <Textarea value={selectedItem.proposals_recommendations || ''} onChange={e => setSelectedItem({ ...selectedItem, proposals_recommendations: e.target.value })}
          onBlur={() => updateAgendaItem(selectedItem.id, { proposals_recommendations: selectedItem.proposals_recommendations })}
          placeholder="Proposals or recommendations to address the issues..." className="mt-1 border-[#E2E6EA] min-h-[60px]" data-testid="proposals-recommendations" />
      </div>

      {/* 3) Requested Action */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21]">3. Requested Action to the Board/Committee</Label>
        <Select value={selectedItem.requested_action || ''} onValueChange={v => { setSelectedItem({ ...selectedItem, requested_action: v }); updateAgendaItem(selectedItem.id, { requested_action: v }); }}>
          <SelectTrigger className="mt-1 border-[#E2E6EA]" data-testid="requested-action"><SelectValue placeholder="Select action type" /></SelectTrigger>
          <SelectContent>{REQUESTED_ACTION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* 4) Deliberations */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21]">4. Board/Committee Deliberations</Label>
        <Textarea value={selectedItem.deliberations || ''} onChange={e => setSelectedItem({ ...selectedItem, deliberations: e.target.value })}
          onBlur={() => updateAgendaItem(selectedItem.id, { deliberations: selectedItem.deliberations })}
          placeholder="Record the deliberations..." className="mt-1 border-[#E2E6EA] min-h-[60px]" data-testid="deliberations" />
      </div>

      {/* 5) Motions */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21]">5. Motions</Label>
        <Textarea value={selectedItem.motion_text || ''} onChange={e => setSelectedItem({ ...selectedItem, motion_text: e.target.value })}
          onBlur={() => updateAgendaItem(selectedItem.id, { motion_text: selectedItem.motion_text })}
          placeholder="Enter the motion text..." className="mt-1 border-[#E2E6EA]" data-testid="motion-text" />
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div><Label className="text-xs">Moved By</Label>
            <Select value={selectedItem.mover || ''} onValueChange={v => { setSelectedItem({ ...selectedItem, mover: v }); updateAgendaItem(selectedItem.id, { mover: v }); }}>
              <SelectTrigger className="border-[#E2E6EA] h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Seconded By</Label>
            <Select value={selectedItem.seconder || ''} onValueChange={v => { setSelectedItem({ ...selectedItem, seconder: v }); updateAgendaItem(selectedItem.id, { seconder: v }); }}>
              <SelectTrigger className="border-[#E2E6EA] h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* 6) For Voting */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21]">6. For Voting</Label>
        <p className="text-xs text-[#5A6570] mt-0.5 mb-1">Define the subject matter or topic to be voted upon. This will appear in the Voting tab.</p>
        <Textarea value={selectedItem.for_voting_subject || ''} onChange={e => setSelectedItem({ ...selectedItem, for_voting_subject: e.target.value })}
          onBlur={() => updateAgendaItem(selectedItem.id, { for_voting_subject: selectedItem.for_voting_subject })}
          placeholder="State the subject matter or resolution to be voted upon..." className="mt-1 border-[#E2E6EA] min-h-[60px]" data-testid="for-voting-subject" />
      </div>

      {/* 7) Agreements */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21]">7. Agreements</Label>
        <Textarea value={selectedItem.agreements || ''} onChange={e => setSelectedItem({ ...selectedItem, agreements: e.target.value })}
          onBlur={() => updateAgendaItem(selectedItem.id, { agreements: selectedItem.agreements })}
          placeholder="Record the agreements reached..." className="mt-1 border-[#E2E6EA]" data-testid="agreements" />
      </div>

      {/* 8) Action Items */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21] mb-2 block">8. Action Items</Label>
        {(selectedItem.action_items_detail || []).length > 0 && (
          <div className="space-y-1.5 mb-3">
            {RESPONSIBLE_GROUPS.map(g => {
              const items = (selectedItem.action_items_detail || []).filter(a => a.group === g.value);
              if (!items.length) return null;
              return (
                <div key={g.value}>
                  <p className="text-xs font-medium text-[#5A6570] mb-1">{g.label}</p>
                  {items.map(ai => (
                    <div key={ai.id} className="flex items-center justify-between p-2 rounded border border-[#E2E6EA] mb-1">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#1A1D21]">{ai.description}</p>
                        {ai.responsible && <p className="text-[10px] text-[#5A6570]">Responsible: {ai.responsible}</p>}
                      </div>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 shrink-0" onClick={() => removeActionItemFromDetail(ai.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
        <div className="p-3 bg-[#F8F9FA] rounded-lg border border-[#E2E6EA] space-y-2">
          <Input value={newAI.description} onChange={e => setNewAI({ ...newAI, description: e.target.value })} placeholder="Task description" className="border-[#E2E6EA] h-8 text-sm" data-testid="new-action-description" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={newAI.responsible} onChange={e => setNewAI({ ...newAI, responsible: e.target.value })} placeholder="Person responsible" className="border-[#E2E6EA] h-8 text-sm" />
            <Select value={newAI.group} onValueChange={v => setNewAI({ ...newAI, group: v })}>
              <SelectTrigger className="border-[#E2E6EA] h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{RESPONSIBLE_GROUPS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={addActionItemToDetail} className="bg-[#00A3A3] hover:bg-[#008585] h-7 text-xs" data-testid="add-action-btn"><Plus className="h-3 w-3 mr-1" />Add Action Item</Button>
        </div>
      </div>
    </div>
  );
};
